import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Users, Newspaper, Briefcase, HeartHandshake, UserCheck } from 'lucide-react';
import { postService } from '../services/postService';
import { workerService } from '../services/workerService';
import { userService } from '../services/userService';

export default function DashboardPage() {
  const [stats, setStats] = useState({
    totalPosts: 0,
    news: 0,
    projects: 0,
    partners: 0,
    workers: 0,
    users: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [posts, workers, users] = await Promise.all([
          postService.getAll(),
          workerService.getAll(),
          userService.getAll(),
        ]);

        setStats({
          totalPosts: posts.length,
          news: posts.filter((p) => p.post_type === 1 || p.post_type === 'NEWS').length,
          projects: posts.filter((p) => p.post_type === 2 || p.post_type === 'PROJECT').length,
          partners: posts.filter((p) => p.post_type === 3 || p.post_type === 'PARTNER').length,
          workers: workers.length,
          users: users.length,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      name: 'Всего постов',
      value: stats.totalPosts,
      icon: FileText,
      href: '/admin/posts',
      color: 'bg-blue-500',
    },
    {
      name: 'Новости',
      value: stats.news,
      icon: Newspaper,
      href: '/admin/posts?type=NEWS',
      color: 'bg-green-500',
    },
    {
      name: 'Проекты',
      value: stats.projects,
      icon: Briefcase,
      href: '/admin/posts?type=PROJECT',
      color: 'bg-purple-500',
    },
    {
      name: 'Партнёры',
      value: stats.partners,
      icon: HeartHandshake,
      href: '/admin/posts?type=PARTNER',
      color: 'bg-yellow-500',
    },
    {
      name: 'Сотрудники',
      value: stats.workers,
      icon: Users,
      href: '/admin/workers',
      color: 'bg-orange-500',
    },
    {
      name: 'Пользователи',
      value: stats.users,
      icon: UserCheck,
      href: '/admin/users',
      color: 'bg-teal-500',
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Панель управления</h1>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="bg-white overflow-hidden rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.color} rounded-md p-3`}>
                  <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {loading ? (
                        <div className="h-8 w-12 bg-gray-200 rounded animate-pulse"></div>
                      ) : (
                        stat.value
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Быстрые действия</h2>
          <div className="space-y-3">
            <Link
              to="/admin/posts/new"
              className="flex items-center p-3 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
            >
              <FileText className="h-5 w-5 mr-3" />
              Создать новый пост
            </Link>
            <Link
              to="/admin/workers/new"
              className="flex items-center p-3 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
            >
              <Users className="h-5 w-5 mr-3" />
              Добавить сотрудника
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Информация</h2>
          <div className="text-sm text-gray-600 space-y-2">
            <p>Добро пожаловать в админ-панель Zhasyl Zhelken!</p>
            <p>Здесь вы можете управлять контентом сайта:</p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>Создавать и редактировать новости</li>
              <li>Управлять проектами</li>
              <li>Добавлять информацию о партнёрах</li>
              <li>Управлять списком сотрудников</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
